import { Box, Button, CarouselWIP, Loader, TextButton } from '@wix/design-system'
import { useContent, useNext } from '@wix/funnel-builder-lizard'
import { ChevronLeftLargeSmall } from '@wix/wix-ui-icons-common'
import React, { FC, useEffect, useState } from 'react'

import { usePageLogger } from '../../../../bi/mfi'
import { useFlowContext } from '../../../../hooks/useFlowContext'
import { WixMobileBackButton } from '../../../reusable/WixMobileBackButton/WixMobileBackButton'
import { WixMobileContentWrapper } from '../../../reusable/WixMobileContentWrapper/WixMobileContentWrapper'
import { WixMobileFooter } from '../../../reusable/WixMobileFooter/WixMobileFooter'
import { WixMobileHeader } from '../../../reusable/WixMobileHeader/WixMobileHeader'
import { WixMobileHeading } from '../../../reusable/WixMobileHeading/WixMobileHeading'
import { WixMobilePage } from '../../../reusable/WixMobilePage/WixMobilePage'
import { WixMobileSkipButton } from '../../../reusable/WixMobileSkipButton/WixMobileSkipButton'
import { WixMobileStepProgressBar } from '../../../reusable/WixMobileStepProgressBar/WixMobileStepProgressBar'
import { Content, TemplateItem } from './StoresFunnelChooseTemplate.data'
import { storesFunnelChooseTemplateDataHooks as DataHooks } from './StoresFunnelChooseTemplate.dataHooks'
import s from './StoresFunnelChooseTemplate.scss'
import { TemplateCarouselItem } from './TemplateCarouselItem/TemplateCarouselItem'
import { templatesMap } from './templates'

export const StoresFunnelChooseTemplate: FC = () => {
  const [flowContext] = useFlowContext()
  const [isPreviewLoading, setIsPreviewLoading] = useState(true)
  const [showPreview, setShowPreview] = useState(false)
  const { content } = useContent<Content>()
  const next = useNext()
  const pageLogger = usePageLogger()

  const { title } = content.question.content
  const category = flowContext?.answers[content.category_key]?.values?.[0] || '' || 'something_else'
  const templates = templatesMap[category]
  const [selectedTemplate, setSelectedTemplate] = useState<TemplateItem>(templates[0])

  const isShowPreviewModal = showPreview && selectedTemplate

  useEffect(() => {
    void pageLogger.screenView()
  }, [pageLogger])

  const onSkipButtonClick = () => {
    pageLogger.skip({
      payload: {},
    })

    next({ [content.question.questionKey]: { skipped: true } })
  }

  const onNextButtonClick = () => {
    if (selectedTemplate) {
      pageLogger.answer({
        answer: { templateId: selectedTemplate.templateId },
      })

      next({ [content.question.questionKey]: { values: [selectedTemplate.templateId] } })
    }
  }

  const onBackButtonClick = () => {
    pageLogger.back({
      payload: {},
    })

    window.history.back()
  }

  const onTemplateSelect = (template: TemplateItem) => {
    pageLogger.clickAnswer({
      questionKey: content.question.questionKey,
      buttonName: template.templateId,
    })
    setSelectedTemplate(template)
  }

  const onPreviewTemplate = (template: TemplateItem) => {
    pageLogger.clickAnswer({
      questionKey: content.question.questionKey,
      buttonName: `${template.templateId}_preview`,
    })
    setSelectedTemplate(template)
    setShowPreview(true)
  }

  const onClosePreview = () => {
    setShowPreview(false)
  }

  const onChooseTemplate = () => {
    if (selectedTemplate) {
      pageLogger.answer({
        answer: { templateId: selectedTemplate.templateId },
      })

      next({ [content.question.questionKey]: { values: [selectedTemplate.templateId] } })
    }
  }

  useEffect(() => setIsPreviewLoading(true), [selectedTemplate])

  return (
    <WixMobilePage>
      {/* NOTE: preview template modal window */}
      {isShowPreviewModal && (
        <div className={s.modalContainer}>
          <div className={s.modalHeader}>
            <TextButton priority="secondary" skin="light" onClick={onClosePreview} data-hook={DataHooks.modalBack()}>
              <Box gap="4px" verticalAlign="middle">
                <ChevronLeftLargeSmall /> {content.back}
              </Box>
            </TextButton>
            <Button priority="primary" skin="light" onClick={onChooseTemplate} data-hook={DataHooks.modalChoose()}>
              {content.chooseTemplate}
            </Button>
          </div>
          <Box flex="1" width="100%">
            {isPreviewLoading && (
              <Box position="absolute" top="0" right="0" bottom="0" left="0" align="center" verticalAlign="middle">
                <Loader />
              </Box>
            )}
            <iframe
              title={selectedTemplate.title}
              onLoad={() => setIsPreviewLoading(false)}
              src={`https://www.wix.com/studio/templates/api/preview/${selectedTemplate.templateId}/mobile`}
              className={s.modalIframe}
              data-hook={DataHooks.iframe()}
            />
          </Box>
        </div>
      )}

      <WixMobileHeader
        leftContent={
          (content.back && (
            <WixMobileBackButton dataHook={DataHooks.back()} onBack={onBackButtonClick} label={content.back} />
          )) ||
          undefined
        }
        rightContent={
          (content.skip && (
            <WixMobileSkipButton dataHook={DataHooks.skip()} onSkip={onSkipButtonClick} label={content.skip} />
          )) ||
          undefined
        }
      />
      <WixMobileContentWrapper>
        {content.stepProgressBar.content.visible && (
          <WixMobileStepProgressBar
            current={content.stepProgressBar.content.current}
            total={content.stepProgressBar.content.total}
            dataHook={DataHooks.stepProgressBar()}
          />
        )}
        <WixMobileHeading dataHook={DataHooks.title()}>{title}</WixMobileHeading>

        <div className={s.carouselRoot}>
          <CarouselWIP
            infinite={false}
            controlsSkin="transparent"
            controlsPosition="overlay"
            controlsStartEnd="hidden"
            slidingType="align-to-start"
            gutter={30}
            sidesGradientColor="#ffffff"
          >
            {templates.map(template => (
              <TemplateCarouselItem
                key={template.templateId}
                template={template}
                isSelected={selectedTemplate?.templateId === template.templateId}
                onSelect={() => onTemplateSelect(template)}
                previewButtonText={content.preview}
                onPreview={() => onPreviewTemplate(template)}
              />
            ))}
          </CarouselWIP>
        </div>
      </WixMobileContentWrapper>
      {content.next && (
        <WixMobileFooter>
          <Button
            size="large"
            fullWidth
            disabled={!selectedTemplate}
            onClick={onNextButtonClick}
            dataHook={DataHooks.next()}
          >
            {content.next}
          </Button>
        </WixMobileFooter>
      )}
    </WixMobilePage>
  )
}
